import { CONTRACT_STATUSES, CONTRACT_STATUS_ENUM, ContractStatus } from "interfaces";
import type { ContractHistoryRow } from "services/contractService";

export type { ContractHistoryRow };

export type ContractStepPayload = {
  rawStatus: ContractStatus;
  title: string;
  /**
   * Все записи истории по этому статусу — для раскрывающегося блока (дата, ФИО, комментарий).
   * Пусто для «будущих» шагов или если по статусу нет строк в ответе.
   */
  detailRows: ContractHistoryRow[];
};

export type ContractStepsViewModel = {
  items: ContractStepPayload[];
  currentIndex: number;
  currentStatus: ContractStatus;
};

function getFutureStepsFromLastStatus(lastStatus: ContractStatus | undefined): ContractStatus[] {
  if (lastStatus === CONTRACT_STATUS_ENUM.DRAFT) {
    return [CONTRACT_STATUS_ENUM.ACTIVE, CONTRACT_STATUS_ENUM.PAID];
  }

  if (lastStatus === CONTRACT_STATUS_ENUM.ACTIVE) {
    return [CONTRACT_STATUS_ENUM.PAID];
  }

  return [];
}

function getVisibleOrderForEmptyHistory(cardStatus: ContractStatus): ContractStatus[] {
  return [cardStatus, ...getFutureStepsFromLastStatus(cardStatus)];
}

/**
 * Источник истины — порядок статусов из history.
 * Каждая запись history — отдельный шаг, даже если статусы повторяются.
 * Если history пустая, не синтезируем «прошлые» шаги: берем текущий статус карточки
 * и достраиваем только допустимые будущие шаги от него.
 * Будущие шаги добавляем только от последнего фактического статуса:
 * DRAFT -> ACTIVE, PAID
 * ACTIVE -> PAID
 * PAID / DRAFT_DECLINED / BLOCKED -> без будущих шагов
 */
export function getVisibleContractStepOrder(
  fallbackCardStatus: ContractStatus,
  timeline: ContractHistoryRow[]
): ContractStatus[] {
  if (timeline.length === 0) {
    return getVisibleOrderForEmptyHistory(fallbackCardStatus);
  }

  const lastStatus = timeline.at(-1)?.status;

  return [...timeline.map((row) => row.status), ...getFutureStepsFromLastStatus(lastStatus)];
}

function buildContractStepPayloads(
  rows: ContractHistoryRow[],
  fallbackCardStatus: ContractStatus
): ContractStepPayload[] {
  if (rows.length === 0) {
    return getVisibleOrderForEmptyHistory(fallbackCardStatus).map((stepStatus) => ({
      rawStatus: stepStatus,
      title: CONTRACT_STATUSES.texts[stepStatus],
      detailRows: [],
    }));
  }

  const historyItems = rows.map((row) => ({
    rawStatus: row.status,
    title: CONTRACT_STATUSES.texts[row.status],
    detailRows: [row],
  }));
  const futureItems = getFutureStepsFromLastStatus(rows.at(-1)?.status).map((stepStatus) => ({
    rawStatus: stepStatus,
    title: CONTRACT_STATUSES.texts[stepStatus],
    detailRows: [],
  }));

  return [...historyItems, ...futureItems];
}

function getCurrentContractStepStatus(rows: ContractHistoryRow[], fallbackCardStatus: ContractStatus): ContractStatus {
  return rows.at(-1)?.status ?? fallbackCardStatus;
}

function getCurrentContractStepIndex(items: ContractStepPayload[], currentStatus: ContractStatus): number {
  const index = items.findLastIndex((item) => item.rawStatus === currentStatus);
  return index >= 0 ? index : items.length > 0 ? items.length - 1 : -1;
}

/** Чистая функция: history + fallback статуса карточки для пустой history → шаги для UI. */
export function buildContractStepsViewModel(
  rows: ContractHistoryRow[],
  fallbackCardStatus: ContractStatus
): ContractStepsViewModel {
  const items = buildContractStepPayloads(rows, fallbackCardStatus);
  const currentStatus = getCurrentContractStepStatus(rows, fallbackCardStatus);
  const currentIndex = getCurrentContractStepIndex(items, currentStatus);

  return {
    items,
    currentIndex,
    currentStatus,
  };
}
